import { z } from 'zod';
import { paginationQuery } from './common.js';
import { IVA_RATES } from './hacienda.js';
/** Precio opcional (listas 2–4): vacío, nulo o NaN = sin precio. Misma precisión y tope que `price`. */
const optionalPrice = z.preprocess((v) => (v === '' || v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v)) ? null : v), z.coerce.number().min(0, 'No puede ser negativo').max(1e12).nullable());
const ivaCodes = IVA_RATES.map((r) => r.code);
export const productInput = z.object({
    code: z
        .string()
        .trim()
        .max(40)
        .transform((v) => v || null)
        .nullable()
        .optional(),
    name: z.string().trim().min(2, 'Requerido').max(200),
    description: z
        .string()
        .trim()
        .max(1000)
        .transform((v) => v || null)
        .nullable()
        .optional(),
    cabysCode: z
        .string()
        .regex(/^\d{13}$/, 'El CABYS tiene 13 dígitos')
        .nullable()
        .optional(),
    unit: z.string().min(1).max(20).default('Unid'),
    categoryId: z.uuid().nullable().optional(),
    price: z.coerce.number().min(0, 'No puede ser negativo').max(1e12),
    /** Precios 2 a 4 (opcionales, en la misma moneda). El Precio 1 es `price`. */
    price2: optionalPrice.optional(),
    price3: optionalPrice.optional(),
    price4: optionalPrice.optional(),
    currency: z.enum(['CRC', 'USD', 'EUR']).default('CRC'),
    ivaCode: z.enum(ivaCodes).default('08'),
    /** El precio ya incluye IVA (se desglosa al facturar). */
    priceIncludesTax: z.boolean().default(false),
    pharmaRegistry: z
        .string()
        .trim()
        .max(100)
        .transform((v) => v || null)
        .nullable()
        .optional(),
    pharmaForm: z
        .string()
        .trim()
        .max(10)
        .transform((v) => v || null)
        .nullable()
        .optional(),
});
export const productListQuery = paginationQuery.extend({
    categoryId: z.uuid().optional(),
    status: z.enum(['active', 'archived', 'all']).default('active'),
    kind: z.enum(['good', 'service']).optional(),
});
export const categoryInput = z.object({ name: z.string().trim().min(2, 'Requerido').max(80) });
/* ------------------------- listas de precio y precios por cliente ------------------------- */
export const PRICE_TIERS = [1, 2, 3, 4];
export const PRICE_TIER_LABEL = {
    1: 'Precio 1',
    2: 'Precio 2',
    3: 'Precio 3',
    4: 'Precio 4',
};
/** Precio del producto en la lista pedida; `null` si esa lista no tiene valor (el Precio 1 siempre lo tiene). */
export function tierPrice(product, tier) {
    if (tier === 2)
        return product.price2 ?? null;
    if (tier === 3)
        return product.price3 ?? null;
    if (tier === 4)
        return product.price4 ?? null;
    return product.price;
}
/**
 * Precio de un producto al armar una línea: (a) el precio específico del cliente; (b) el de la lista elegida;
 * (c) el Precio 1 (base) si esa lista no tiene valor para el producto.
 */
export function resolveProductPrice(product, options) {
    if (options.customerPrice !== null && options.customerPrice !== undefined)
        return { price: options.customerPrice, source: 'customer', tier: 1 };
    const tier = options.tier ?? 1;
    const value = tierPrice(product, tier);
    return value === null
        ? { price: product.price, source: 'list', tier: 1 }
        : { price: value, source: 'list', tier };
}
/** Etiqueta discreta del origen del precio de una línea. */
export function priceSourceLabel(resolved) {
    return resolved.source === 'customer' ? 'Precio cliente' : PRICE_TIER_LABEL[resolved.tier];
}
export const productCustomerPriceInput = z.object({
    price: z.coerce.number().min(0, 'No puede ser negativo').max(1e12),
});
//# sourceMappingURL=products.js.map